import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Minus, Plus, ShoppingCart, X } from 'lucide-react';
import { fnbAPI } from '../services/api';

const formatCurrency = (v) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(v || 0);

export default function QrOrder() {
  const { tenantId, tableId } = useParams();
  const [tenantName, setTenantName] = useState('');
  const [table, setTable] = useState(null);
  const [menus, setMenus] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [note, setNote] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const [menuRes, tableRes] = await Promise.all([
          fnbAPI.getPublicMenu(tenantId),
          fnbAPI.getPublicTable(tenantId, tableId),
        ]);
        if (!active) return;
        setTenantName(menuRes.data?.tenant_name || 'Stockara FnB');
        setMenus(menuRes.data?.menus || []);
        setTable(tableRes.data);
      } catch (err) {
        console.error(err);
        if (active) setError('Tenant atau meja tidak ditemukan.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [tenantId, tableId]);

  const total = useMemo(() => cart.reduce((sum, item) => sum + item.qty * item.price, 0), [cart]);
  const cartQty = useMemo(() => cart.reduce((sum, item) => sum + item.qty, 0), [cart]);

  const addToCart = (menu) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.menu_id === menu.id);
      if (existing) return prev.map((item) => item.menu_id === menu.id ? { ...item, qty: item.qty + 1 } : item);
      return [...prev, { menu_id: menu.id, menu_name: menu.name, qty: 1, unit_price: menu.price, price: menu.price, note: '' }];
    });
  };
  const updateQty = (menuId, delta) => setCart((prev) => prev
    .map((item) => item.menu_id === menuId ? { ...item, qty: Math.max(0, item.qty + delta) } : item)
    .filter((item) => item.qty > 0));

  const submitOrder = async () => {
    if (!cart.length) return;
    try {
      setSubmitting(true);
      const res = await fnbAPI.submitPublicOrder(tenantId, {
        table_id: tableId,
        table_no: table?.table_no || table?.name || '',
        customer_name: customerName,
        items: cart,
        note,
      });
      setSuccess(res.data);
      setShowCart(false);
      setCart([]);
    } catch (err) {
      console.error(err);
      alert('Gagal mengirim pesanan. Coba lagi.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-sm text-gray-500">Memuat menu...</div>;
  if (error) return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 text-center text-sm text-red-500">{error}</div>;
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto text-2xl">✓</div>
          <h1 className="text-xl font-bold text-gray-900 mt-4">Pesanan dikirim!</h1>
          <p className="text-sm text-gray-500 mt-2">Silakan tunggu. Nomor order kamu: <span className="font-semibold text-gray-900">{success.order_no}</span></p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      <div className="max-w-md mx-auto bg-white min-h-screen">
        <header className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4">
          <p className="text-sm text-emerald-600 font-semibold">{tenantName}</p>
          <div className="flex items-end justify-between mt-1">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Scan & Order</h1>
              <p className="text-sm text-gray-500">Meja {table?.table_no || table?.name || '-'}</p>
            </div>
            <ShoppingCart className="text-gray-400" size={24} />
          </div>
        </header>

        <main className="p-4 grid grid-cols-2 gap-3">
          {menus.map((menu) => {
            const qty = cart.find((item) => item.menu_id === menu.id)?.qty || 0;
            return (
              <button key={menu.id} onClick={() => addToCart(menu)} className="relative bg-white border border-gray-200 rounded-xl p-3 text-left active:scale-95 transition">
                {qty > 0 && <span className="absolute top-2 right-2 bg-emerald-500 text-white text-xs font-bold rounded-full min-w-6 h-6 px-2 flex items-center justify-center">{qty}</span>}
                {menu.image_url ? (
                  <img src={menu.image_url} alt={menu.name} className="w-full h-24 object-cover rounded-lg mb-2" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <div className="w-full h-24 bg-gray-100 rounded-lg mb-2 flex items-center justify-center text-3xl">🍽️</div>
                )}
                <p className="text-sm font-semibold text-gray-900 line-clamp-2">{menu.name}</p>
                {menu.category && <p className="text-xs text-gray-400 mt-0.5">{menu.category}</p>}
                <p className="text-sm font-bold text-emerald-600 mt-1">{formatCurrency(menu.price)}</p>
              </button>
            );
          })}
        </main>
      </div>

      {cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
          <div className="max-w-md mx-auto flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-gray-500">{cartQty} item</p>
              <p className="font-bold text-gray-900">{formatCurrency(total)}</p>
            </div>
            <button onClick={() => setShowCart(true)} className="px-5 py-3 bg-emerald-500 text-white rounded-xl text-sm font-bold">Pesan Sekarang</button>
          </div>
        </div>
      )}

      {showCart && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
          <div className="max-w-md w-full bg-white rounded-t-2xl p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Keranjang</h2>
              <button onClick={() => setShowCart(false)}><X size={20} /></button>
            </div>
            <div className="space-y-3">
              {cart.map((item) => (
                <div key={item.menu_id} className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{item.menu_name}</p>
                    <p className="text-xs text-gray-500">{formatCurrency(item.unit_price)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(item.menu_id, -1)} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center"><Minus size={14} /></button>
                    <span className="w-5 text-center text-sm font-bold">{item.qty}</span>
                    <button onClick={() => updateQty(item.menu_id, 1)} className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center"><Plus size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
            <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Nama customer" className="w-full mt-4 px-3 py-3 border border-gray-200 rounded-xl text-sm" />
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan pesanan" className="w-full mt-3 px-3 py-3 border border-gray-200 rounded-xl text-sm resize-none" rows={2} />
            <button onClick={submitOrder} disabled={submitting || !cart.length} className="w-full mt-4 py-3 bg-emerald-500 text-white rounded-xl text-sm font-bold disabled:opacity-50">{submitting ? 'Mengirim...' : `Kirim Pesanan - ${formatCurrency(total)}`}</button>
          </div>
        </div>
      )}
    </div>
  );
}
